/// Tower Home — the web mirror of the iOS 2.8 level-select redesign (retires the
/// world map). A merchandised scroll of rails: smart Continue hero, Daily
/// Challenge, Live Traffic, Career stages, and themed collection shelves of
/// airport-sigil tiles. Reuses the existing LevelDetailModal (difficulty +
/// leaderboard + checkout) for every tap, plus the existing Welcome / What's New
/// onboarding. No Firestore rule changes; reads the same profile + unlock state.
import { useEffect, useMemo, useState } from 'react'
import { LEVEL_REGISTRY, type LevelRegistryEntry } from '../data/loadLevel'
import { useProfileStore } from '../state/profileStore'
import { useUnlocks, isLevelUnlocked } from '../data/purchases'
import { highestCompletedDifficulty, DIFFICULTY_ORDER, type DifficultyName } from '../data/userProfile'
import {
  COLLECTIONS,
  CAREER_STAGES,
  LIVE_LEVEL_IDS,
  ACCENT,
  CAREER_WINGS_GATE,
  dailyDayIndex,
  dailyLevelId,
  isoDayString,
  type CareerStage,
} from '../data/towerHome'
import { LevelSigil } from './LevelSigil'
import { DailyLeaderboardPanel } from './DailyLeaderboardPanel'
import { LevelDetailModal, difficultyKey } from './HomeScreen'
import { WelcomeModal, hasSeenWelcome } from './WelcomeModal'
import { WhatsNewModal } from './WhatsNewModal'
import { hasSeenLatestWhatsNew, markLatestWhatsNewSeen } from '../data/whatsNew'
import { HelpModal } from './HelpModal'
import { SettingsModal } from './SettingsModal'
import { FeedbackModal } from './FeedbackModal'
import { SignInButton, SignedInMenu } from './AuthControls'

const BY_ID = new Map(LEVEL_REGISTRY.map((e) => [e.id, e]))
const present = (ids: string[]) => ids.filter((id) => BY_ID.has(id))

export function TowerHome(props: {
  onPlay: (levelId: string, difficulty: DifficultyName) => void
  onOpenDevBlog: (postId?: string) => void
  onOpenTowerLog: () => void
}) {
  const profileState = useProfileStore((s) => s.state)
  const user = profileState.kind === 'ready' ? profileState.user : null
  const profile = profileState.kind === 'ready' ? profileState.profile : null
  const unlocks = useUnlocks()
  // Global play count + live presence (still fed by profileStore; the world-map
  // home used to surface these — re-added here after the Tower Home swap).
  const playCount = useProfileStore((s) => s.playCount)
  const playingNow = useProfileStore((s) => s.playingNow)

  const [selected, setSelected] = useState<LevelRegistryEntry | null>(null)
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  const [whatsNewMode, setWhatsNewMode] = useState<'auto' | 'manual' | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [stageOpen, setStageOpen] = useState<CareerStage | null>(null)
  const [dailyBoardOpen, setDailyBoardOpen] = useState(false)

  // Onboarding: welcome first; otherwise auto-surface a new What's New. (Same
  // gating as the old home so first-run isn't double-modal'd.)
  useEffect(() => {
    if (!hasSeenWelcome()) setWelcomeOpen(true)
    else if (!hasSeenLatestWhatsNew()) setWhatsNewMode('auto')
  }, [])

  const clearedTier = (id: string): DifficultyName | null => {
    const stats = profile?.levelStats?.[id]
    return stats ? highestCompletedDifficulty(stats) : null
  }
  const lockedOf = (id: string): boolean => {
    const e = BY_ID.get(id)
    return e ? !isLevelUnlocked(e, unlocks) : false
  }
  const open = (id: string) => {
    const e = BY_ID.get(id)
    if (e) {
      setStageOpen(null)
      setSelected(e)
    }
  }

  const totalWings = useMemo(() => {
    let w = 0
    for (const e of LEVEL_REGISTRY) {
      const t = profile?.levelStats?.[e.id]
        ? highestCompletedDifficulty(profile.levelStats[e.id]!)
        : null
      if (t) w += DIFFICULTY_ORDER.indexOf(t) + 1
    }
    return w
  }, [profile])

  // Smart continue target: last viewed → first uncleared career level → tutorial.
  const continueId = useMemo(() => {
    const last = profile?.lastViewedLevel
    if (last && BY_ID.has(last)) return last
    for (const st of CAREER_STAGES) {
      for (const id of st.levelIds) if (BY_ID.has(id) && !clearedTier(id)) return id
    }
    return BY_ID.has('TUTORIAL') ? 'TUTORIAL' : (LEVEL_REGISTRY[0]?.id ?? null)
  }, [profile])

  // Daily challenge: deterministic by UTC day (paid-only). Shared helper so the
  // streak hook in the store picks the same level on the same day.
  const dailyId = useMemo(
    () => dailyLevelId(dailyDayIndex(), LEVEL_REGISTRY.map((e) => e.id)),
    [],
  )

  const firstLocked = useMemo(
    () => LEVEL_REGISTRY.find((e) => !isLevelUnlocked(e, unlocks))?.id ?? null,
    [unlocks],
  )

  const tile = (id: string) => {
    const e = BY_ID.get(id)
    if (!e) return null
    const live = playingNow?.byLevel[id] ?? 0
    return (
      <button
        key={id}
        onClick={() => open(id)}
        className="flex w-[112px] shrink-0 flex-col gap-1.5 text-left"
      >
        <div className="relative">
          <LevelSigil id={id} size={112} completed={clearedTier(id)} free={!!e.isFree} locked={lockedOf(id)} />
          {live > 0 && (
            <span
              title={`${live} playing now`}
              className="absolute bottom-1 left-1 flex items-center gap-1 rounded bg-black/70 px-1 py-px text-[9px] font-bold text-emerald-300 ring-1 ring-white/10"
            >
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              {live}
            </span>
          )}
        </div>
        <span className="truncate text-xs font-semibold text-white/90">{e.displayName}</span>
      </button>
    )
  }

  const rail = (key: string, title: string, icon: string, accent: string, ids: string[], tag?: string) => {
    const list = present(ids)
    if (!list.length) return null
    return (
      <section key={key} className="mt-6">
        <div className="mb-2 flex items-center gap-2 px-4">
          <span className="text-sm" style={{ color: accent }}>{icon}</span>
          <h2 className="text-sm font-bold text-white/90">{title}</h2>
          {tag && <span className="rounded bg-white/10 px-1.5 py-px text-[10px] font-semibold text-white/60">{tag}</span>}
        </div>
        <div className="flex gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {list.map(tile)}
        </div>
      </section>
    )
  }

  const stageLocked = (st: CareerStage) =>
    CAREER_WINGS_GATE && totalWings < st.wingsToUnlock

  const stageCard = (st: CareerStage) => {
    const ids = present(st.levelIds)
    if (!ids.length) return null
    const done = ids.filter((id) => clearedTier(id)).length
    const locked = stageLocked(st)
    return (
      <button
        key={st.id}
        onClick={() => (locked ? undefined : setStageOpen(st))}
        disabled={locked}
        className="flex w-[160px] shrink-0 flex-col gap-2 rounded-2xl bg-white/[0.05] p-3 text-left ring-1 ring-white/10 disabled:cursor-not-allowed"
        style={{ opacity: locked ? 0.5 : 1 }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ACCENT.career }}>Stage</span>
          {done === ids.length ? <span className="text-sm">🏅</span> : locked ? <span className="text-xs">🔒</span> : null}
        </div>
        <div className="line-clamp-2 text-sm font-bold leading-tight">{st.title}</div>
        <div className="flex flex-wrap gap-1">
          {ids.map((id, i) => (
            <span
              key={i}
              className="h-2 w-2 rounded-full"
              style={{ background: clearedTier(id) ? ACCENT.career : 'rgba(255,255,255,0.15)' }}
            />
          ))}
        </div>
        <div className="text-[11px] text-white/50">
          {locked ? `${st.wingsToUnlock}★ to unlock` : `${done}/${ids.length} cleared`}
        </div>
      </button>
    )
  }

  const cont = continueId ? BY_ID.get(continueId) : null
  const resumed = !!(profile?.lastViewedLevel && BY_ID.has(profile.lastViewedLevel))
  const daily = dailyId ? BY_ID.get(dailyId) : null
  const dDay = dailyDayIndex()
  const ds = profile?.daily
  const streakActive =
    !!ds && (ds.lastDate === isoDayString(dDay) || ds.lastDate === isoDayString(dDay - 1))
  const doneToday = ds?.lastDate === isoDayString(dDay)
  const streakLabel = streakActive
    ? `🔥 ${ds!.streak}-day streak${doneToday ? ' · done today ✓' : ' · play to keep it'}`
    : 'Start a daily streak'

  return (
    <div
      className="absolute inset-0 overflow-y-auto text-white"
      style={{ background: 'linear-gradient(to bottom, #080d1c, #000)' }}
    >
      <div className="mx-auto w-full max-w-[480px] pb-12 md:max-w-5xl">
        {/* Header */}
        <header className="flex items-start justify-between gap-2 px-4 pt-5">
          <div>
            <h1 className="text-xl font-black tracking-tight">You're in the tower</h1>
            <p className="mt-0.5 text-xs text-white/50">
              ✈ {totalWings}★ earned{unlocks.fullUnlock ? '' : ' · Full Game locked'}
            </p>
            {(playCount !== null || (playingNow?.total ?? 0) > 0) && (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-white/40">
                {playCount !== null && (
                  <span>
                    Played <span className="font-mono text-white/60">{playCount.toLocaleString()}</span> times
                  </span>
                )}
                {(playingNow?.total ?? 0) > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                    <span className="font-mono text-emerald-300">{playingNow!.total.toLocaleString()}</span> playing now
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setWhatsNewMode('manual')} title="What's new" className="h-8 w-8 rounded-full bg-white/5 text-sm ring-1 ring-white/10 hover:bg-white/10">✨</button>
            <button onClick={() => setHelpOpen(true)} title="How to play" className="h-8 w-8 rounded-full bg-white/5 text-sm font-bold ring-1 ring-white/10 hover:bg-white/10">?</button>
            <button onClick={() => props.onOpenDevBlog()} title="Talk to the developer" className="h-8 w-8 rounded-full bg-white/5 text-sm ring-1 ring-white/10 hover:bg-white/10">💬</button>
            <button onClick={props.onOpenTowerLog} title="Tower Log" className="h-8 w-8 rounded-full bg-white/5 text-sm ring-1 ring-white/10 hover:bg-white/10">📡</button>
            <button onClick={() => setSettingsOpen(true)} title="Settings" className="h-8 w-8 rounded-full bg-white/5 text-sm ring-1 ring-white/10 hover:bg-white/10">⚙</button>
            {user?.isAnonymous ? <SignInButton /> : user ? <SignedInMenu user={user} /> : null}
          </div>
        </header>

        {/* Continue + Daily: stacked on phone, side-by-side on desktop */}
        <div className="mt-4 grid gap-3 px-4 md:grid-cols-2 md:items-stretch">
        {/* Smart Continue hero */}
        {cont && (
          <button
            onClick={() => open(cont.id)}
            className="flex w-full items-center gap-4 rounded-2xl bg-white/[0.06] p-3 text-left ring-1 ring-white/10"
          >
            <LevelSigil id={cont.id} size={104} completed={clearedTier(cont.id)} free={!!cont.isFree} locked={lockedOf(cont.id)} />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                {resumed ? 'Continue' : 'Start here'}
              </div>
              <div className="mt-0.5 line-clamp-2 text-lg font-extrabold leading-tight">{cont.displayName}</div>
              <span className="mt-2 inline-block rounded-full bg-sky-500 px-4 py-1 text-sm font-bold text-white">
                {resumed ? 'Resume →' : 'Play →'}
              </span>
            </div>
          </button>
        )}

        {/* Daily Challenge — paid only (Full Game). Free players see a teaser. */}
        {daily && unlocks.fullUnlock && (
          <div
            className="relative flex w-full items-center gap-3 rounded-2xl p-3 ring-1"
            style={{ background: 'rgba(251,146,60,0.10)', borderColor: 'rgba(251,146,60,0.3)' }}
          >
            <button onClick={() => open(daily.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
              <LevelSigil id={daily.id} size={64} completed={clearedTier(daily.id)} free={!!daily.isFree} locked={lockedOf(daily.id)} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ACCENT.daily }}>Daily Challenge</div>
                <div className="truncate text-sm font-bold">{daily.displayName}</div>
                <div className="text-[11px] text-white/60">{streakLabel}</div>
              </div>
            </button>
            <button
              onClick={() => setDailyBoardOpen(true)}
              title="Today's leaderboard"
              className="shrink-0 self-start rounded-lg bg-black/30 px-2 py-1 text-sm ring-1 ring-white/10 hover:bg-black/50"
            >
              🏆
            </button>
          </div>
        )}
        {daily && !unlocks.fullUnlock && firstLocked && (
          <button
            onClick={() => open(firstLocked)}
            className="flex w-full items-center gap-3 rounded-2xl p-3 text-left ring-1"
            style={{ background: 'rgba(251,146,60,0.07)', borderColor: 'rgba(251,146,60,0.2)' }}
          >
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-black/30 text-2xl">🔒</div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ACCENT.daily }}>Daily Challenge</div>
              <div className="truncate text-sm font-bold">A new airport every day · streaks</div>
              <div className="text-[11px] text-amber-200/80">Unlock the Full Game to play →</div>
            </div>
          </button>
        )}
        </div>

        {/* Live Traffic */}
        {rail('live', 'Live Traffic', '📡', ACCENT.live, LIVE_LEVEL_IDS, 'real planes')}

        {/* Career — stage cards → detail sheet */}
        <div className="mt-7 mb-2 px-4 text-[11px] font-bold uppercase tracking-wider text-yellow-400/80">
          ★ Your career · Novice → Master
        </div>
        <div className="flex gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CAREER_STAGES.map(stageCard)}
        </div>

        {/* Collections */}
        <div className="mt-7 mb-1 px-4 text-[11px] font-bold uppercase tracking-wider text-white/40">
          The world
        </div>
        {COLLECTIONS.map((c) => rail(`col-${c.id}`, c.title, c.icon, c.accent, c.levelIds))}

        {/* Unlock shelf */}
        {!unlocks.fullUnlock && firstLocked && (
          <button
            onClick={() => open(firstLocked)}
            className="mt-7 block w-[calc(100%-2rem)] rounded-2xl p-4 text-left mx-4"
            style={{ background: 'linear-gradient(110deg, rgba(250,204,21,0.16), rgba(251,146,60,0.10))' }}
          >
            <div className="text-sm font-extrabold text-amber-200">🔓 Unlock the Full Game</div>
            <div className="mt-0.5 text-xs text-white/70">Every airport · unlimited live traffic · all difficulties</div>
          </button>
        )}
      </div>

      {/* Level detail (reused) */}
      {selected && (
        <LevelDetailModal
          entry={selected}
          stats={profile?.levelStats?.[selected.id]}
          currentUid={user?.uid ?? null}
          onCancel={() => setSelected(null)}
          onPlay={(difficulty) => {
            localStorage.setItem(difficultyKey(selected.id), difficulty)
            props.onPlay(selected.id, difficulty)
          }}
        />
      )}

      {/* Career stage detail */}
      {stageOpen && (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
          onClick={() => setStageOpen(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-[#0b1020] p-5 text-white ring-1 ring-white/10 sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ACCENT.career }}>
                  Career stage · {stageOpen.wingsToUnlock}★
                </div>
                <h2 className="text-lg font-extrabold">{stageOpen.title}</h2>
              </div>
              <button
                onClick={() => setStageOpen(null)}
                className="h-8 w-8 shrink-0 rounded-full bg-white/5 text-white/70 ring-1 ring-white/10"
              >
                ✕
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">{present(stageOpen.levelIds).map(tile)}</div>
          </div>
        </div>
      )}

      {/* Day-scoped daily leaderboard */}
      {dailyBoardOpen && daily && (
        <DailyLeaderboardPanel
          date={isoDayString(dailyDayIndex())}
          levelDisplayName={daily.displayName}
          currentUid={user?.uid ?? null}
          onClose={() => setDailyBoardOpen(false)}
        />
      )}

      {/* Onboarding + What's New (reused) */}
      {welcomeOpen && (
        <WelcomeModal
          onDismiss={() => {
            setWelcomeOpen(false)
            markLatestWhatsNewSeen()
          }}
        />
      )}
      {whatsNewMode && <WhatsNewModal trigger={whatsNewMode} onDismiss={() => setWhatsNewMode(null)} />}
      {helpOpen && <HelpModal brief={undefined} goals={null} initialTab="concepts" onDismiss={() => setHelpOpen(false)} />}
      {feedbackOpen && <FeedbackModal levelId={null} onDismiss={() => setFeedbackOpen(false)} />}
      {settingsOpen && (
        <SettingsModal onDismiss={() => setSettingsOpen(false)} onShowWelcome={() => setWelcomeOpen(true)} />
      )}
    </div>
  )
}
